import { useEffect, useMemo, useState } from "react";
import { BiBuildingHouse, BiLockAlt, BiUser, BiWorld } from "react-icons/bi";
import { useToast } from "../../../components/Toast/ToastContext";
import { getUser, setUser } from "../../../store/auth/storeToken";
import { COUNTRY_CURRENCY } from "../../../types/countryCurrency";
import {
  AccountSettings,
  AccountSettingsUpdate,
  apiErrorMessage,
  updateAccountSettings,
} from "../../../services/settingsApi";
import { Card, Field, inputClass, primaryButtonClass, secondaryButtonClass } from "../settingsUi";

const SUPPORT_EMAIL = "support@vendororbis.com";

const COUNTRY_OPTIONS = COUNTRY_CURRENCY.map((c) => ({
  value: c.countryCode,
  label: c.countryName,
}));

// One entry per currency, as several countries share one.
const CURRENCY_OPTIONS = Array.from(
  new Map(
    COUNTRY_CURRENCY.map((c) => [
      c.currencyCode,
      { value: c.currencyCode, label: `${c.currencyCode} · ${c.currencyName}` },
    ])
  ).values()
);

type Draft = Pick<
  AccountSettings,
  "name" | "businessName" | "phone" | "address" | "websiteUrl"
> & { country: string; currency: string };

const draftFrom = (settings: AccountSettings): Draft => ({
  name: settings.name,
  businessName: settings.businessName,
  phone: settings.phone,
  address: settings.address,
  websiteUrl: settings.websiteUrl,
  country: settings.country ?? "",
  currency: settings.currency ?? "",
});

type Props = {
  settings: AccountSettings;
  onSaved: (next: AccountSettings) => void;
};

const ProfileSection = ({ settings, onSaved }: Props) => {
  const { notify } = useToast();
  const [draft, setDraft] = useState<Draft>(() => draftFrom(settings));
  const [saving, setSaving] = useState(false);

  useEffect(() => setDraft(draftFrom(settings)), [settings]);

  const original = useMemo(() => draftFrom(settings), [settings]);
  const changes = useMemo(() => {
    const diff: AccountSettingsUpdate = {};
    (Object.keys(draft) as Array<keyof Draft>).forEach((key) => {
      if (draft[key].trim() !== original[key]) (diff as any)[key] = draft[key].trim();
    });
    return diff;
  }, [draft, original]);
  const dirty = Object.keys(changes).length > 0;

  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.name.trim()) {
      notify("Your name can't be empty.", { type: "error" });
      return;
    }
    setSaving(true);
    try {
      const next = await updateAccountSettings(changes);
      // The nav and other pages read the signed-in user from storage.
      const stored = getUser();
      if (stored) setUser({ ...stored, name: next.name, businessName: next.businessName });
      onSaved(next);
      notify("Profile saved.", { type: "success" });
    } catch (error) {
      notify(apiErrorMessage(error, "Couldn't save your profile."), { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-5">
      <Card icon={BiUser} title="You" description="How you appear to your team.">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Your name">
            <input
              className={inputClass}
              value={draft.name}
              onChange={(e) => set({ name: e.target.value })}
              maxLength={120}
              autoComplete="name"
              required
            />
          </Field>
          <Field
            label="Sign-in email"
            hint={
              <>
                Your sign-in email can't be changed. Need help? Email{" "}
                <a className="text-blue-600 hover:underline" href={`mailto:${SUPPORT_EMAIL}`}>
                  {SUPPORT_EMAIL}
                </a>
                .
              </>
            }
          >
            <div className="relative">
              <input className={`${inputClass} pr-9`} value={settings.email} disabled readOnly />
              <BiLockAlt
                aria-hidden
                className="absolute right-3 top-1/2 mt-[3px] -translate-y-1/2 text-slate-400"
              />
            </div>
          </Field>
        </div>
      </Card>

      <Card
        icon={BiBuildingHouse}
        title="Business"
        description="Shown on your reports, forms, and to employees you invite."
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Business name">
            <input
              className={inputClass}
              value={draft.businessName}
              onChange={(e) => set({ businessName: e.target.value })}
              maxLength={120}
              autoComplete="organization"
            />
          </Field>
          <Field label="Phone">
            <input
              className={inputClass}
              value={draft.phone}
              onChange={(e) => set({ phone: e.target.value })}
              maxLength={40}
              type="tel"
              autoComplete="tel"
            />
          </Field>
          <Field label="Website">
            <input
              className={inputClass}
              value={draft.websiteUrl}
              onChange={(e) => set({ websiteUrl: e.target.value })}
              maxLength={300}
              placeholder="yourshop.com"
              autoComplete="url"
            />
          </Field>
          <Field label="Business address">
            <input
              className={inputClass}
              value={draft.address}
              onChange={(e) => set({ address: e.target.value })}
              maxLength={300}
              autoComplete="street-address"
            />
          </Field>
        </div>
      </Card>

      <Card icon={BiWorld} title="Region" description="Your country and the currency your prices are in.">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Country">
            <select
              className={inputClass}
              value={draft.country}
              onChange={(e) => set({ country: e.target.value })}
            >
              {!draft.country && <option value="">Select country</option>}
              {draft.country && !COUNTRY_OPTIONS.some((o) => o.value === draft.country) && (
                <option value={draft.country}>{draft.country}</option>
              )}
              {COUNTRY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Currency"
            hint="Used for your business insights. Amounts you've already entered aren't converted."
          >
            <select
              className={inputClass}
              value={draft.currency}
              onChange={(e) => set({ currency: e.target.value })}
            >
              {!draft.currency && <option value="">Select currency</option>}
              {draft.currency && !CURRENCY_OPTIONS.some((o) => o.value === draft.currency) && (
                <option value={draft.currency}>{draft.currency}</option>
              )}
              {CURRENCY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </Card>

      <div className="sticky bottom-4 flex justify-end gap-2">
        {dirty && (
          <button
            type="button"
            className={secondaryButtonClass}
            onClick={() => setDraft(original)}
            disabled={saving}
          >
            Discard
          </button>
        )}
        <button type="submit" className={primaryButtonClass} disabled={!dirty || saving}>
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </form>
  );
};

export default ProfileSection;
